import React, {useState} from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Tooltip
} from '@mui/material'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import format from 'date-fns/format'
import { SubscriptionDetails } from 'types/api/faber/dataUsage.type'

import dataApi from "../../../../api/faber/DataApi";

function SubscriptionTable({ subscriptionData, userId, faberToken }: { subscriptionData: SubscriptionDetails[], userId: string, faberToken: string }) {
    const [openDialog, setOpenDialog] = useState(false)
    const [cancelReason, setCancelReason] = useState('')
    const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionDetails | null>(null)

    const handleOpenDialog = (subscription: SubscriptionDetails) => {
        setSelectedSubscription(subscription)
        setOpenDialog(true)
    }

    const handleCloseDialog = () => {
        setCancelReason('')
        setSelectedSubscription(null)
        setOpenDialog(false)
    }

    const handleConfirmCancellation = async () => {
        if (!selectedSubscription) return

        try {
            await dataApi.cancelUserSubscription(faberToken, userId, {
                reasons: [cancelReason]
            })
            console.log('Subscription cancelled successfully')
        } catch (error) {
            console.error('Failed to cancel subscription:', error)
        } finally {
            handleCloseDialog()
        }
    }

    const subscriptionColumns = [
        {
            field: 'name',
            headerName: 'Product Name',
            sortable: false,
            minWidth: 100
        },
        {
            field: 'status',
            headerName: 'Status',
            sortable: false,
            minWidth: 30
        },
        {
            field: 'billingCycleAnchor',
            headerName: 'Subscription Start',
            minWidth: 100,
            valueFormatter: ({ value }) => format(new Date(value), 'dd MMM yyyy HH:mm')
        },
        {
            field: 'currentPeriodStart',
            headerName: 'Current Period Start',
            minWidth: 100,
            valueFormatter: ({ value }) => format(new Date(value), 'dd MMM yyyy HH:mm')
        },
        {
            field: 'currentPeriodEnd',
            headerName: 'Current Period End',
            minWidth: 100,
            valueFormatter: ({ value }) => format(new Date(value), 'dd MMM yyyy HH:mm')
        },
        {
            field: 'earliestCancellationDate',
            headerName: 'Earliest Cancellation Date',
            minWidth: 100,
            valueFormatter: ({ value }) => format(new Date(value), 'dd MMM yyyy HH:mm')
        },
        {
            field: 'actions',
            headerName: 'Actions',
            sortable: false,
            minWidth: 200,
            renderCell: (params) => (
                <Tooltip title="This will immediately cancel the subscription">
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleOpenDialog(params.row)}
                    >
                        Cancel Subscription
                    </Button>
                </Tooltip>
            )
        }
    ]

    return (
        <>
            <MUIDataGrid
                columns={subscriptionColumns}
                rows={Array.isArray(subscriptionData) ? subscriptionData : []}
                getRowId={(row) => row.name + String(row.currentPeriodStart)}
                autoHeight
            />

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Cancel Subscription</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Reason for cancellation"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmCancellation}
                        color="error"
                        variant="contained"
                        disabled={!cancelReason.trim()} // Disable if input is empty or whitespace
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default SubscriptionTable
